/**
 * API通信の基底クライアント
 * 全てのAPI呼び出しで使用する共通関数を提供
 */
import { getAnalyticsPage, trackEvent } from '../utils/analytics';

// 環境変数から取得し、末尾のスラッシュを削除
const API_BASE = (import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');

function nowMs() {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

function getDurationMs(startMs) {
  return Math.max(0, Math.round(nowMs() - startMs));
}

function getEndpoint(path) {
  return path.startsWith('/') ? path : `/${path}`;
}

async function request(method, path, fetchOptions, requestUrl = `${API_BASE}${path}`) {
  const startedAt = nowMs();
  const endpoint = getEndpoint(path);
  let status;

  try {
    const res = await fetch(requestUrl, fetchOptions);
    status = res.status;
    const durationMs = getDurationMs(startedAt);

    if (!res.ok) {
      const error = new Error(`API Error ${res.status}: ${res.statusText}`);
      error.status = res.status;
      throw error;
    }

    trackEvent('api_request_success', {
      endpoint,
      method,
      duration_ms: durationMs,
      status,
      page: getAnalyticsPage(),
    });

    return res.json();
  } catch (error) {
    if (error?.name !== 'AbortError') {
      trackEvent('api_request_error', {
        endpoint,
        method,
        duration_ms: getDurationMs(startedAt),
        error: error?.message || 'Unknown error',
        page: getAnalyticsPage(),
      });
    }
    throw error;
  }
}

/**
 * POST リクエストを送信
 * @param {string} path - APIパス（例: '/api/get-graph'）
 * @param {Object} body - リクエストボディ
 * @param {Object} options - オプション（signal等）
 * @returns {Promise<Object>} レスポンスJSON
 */
export async function apiPost(path, body, options = {}) {
  const { signal } = options;

  return request('POST', path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
}

/**
 * GET リクエストを送信
 * @param {string} path - APIパス
 * @param {Object} params - クエリパラメータ
 * @param {Object} options - オプション（signal等）
 * @returns {Promise<Object>} レスポンスJSON
 */
export async function apiGet(path, params = {}, options = {}) {
  const { signal } = options;
  const url = new URL(`${API_BASE}${path}`);

  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== '') {
      url.searchParams.set(key, value);
    }
  });

  return request('GET', path, { signal }, url.toString());
}
